import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { validarEmail, validarRut, formatearRut, contienePalabrasOfensivas } from '../utils/validaciones';
import './Dashboard.css'

const Funcionarios = () => {
  const navigate = useNavigate();
  const [funcionarios, setFuncionarios] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroCargo, setFiltroCargo] = useState('');
  const [profanityList, setProfanityList] = useState('');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ nombre: '', apellido: '', email: '', rut: '', cargo: '' });
  const [formErrors, setFormErrors] = useState({});

  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedFuncionario, setSelectedFuncionario] = useState(null);
  const [funcionarioEquipos, setFuncionarioEquipos] = useState([]);
  const [isEquiposLoading, setIsEquiposLoading] = useState(false);

  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const apiBase = isLocal ? `http://${window.location.hostname}:3001` : `https://${window.location.hostname}`;


  const fetchFuncionarios = async () => {
    setIsLoading(true);
    try {
      const resp = await fetch(`${apiBase}/api/funcionarios`);
      if (resp.ok) {
        const data = await resp.json();
        setFuncionarios(data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProfanityList = async () => {
    try {
      const resp = await fetch(`${apiBase}/api/config/profanity`);
      if (resp.ok) {
        const data = await resp.json();
        setProfanityList(data.words);
      }
    } catch (err) { console.error(err); }
  };

  useEffect(() => {
    fetchFuncionarios();
    fetchProfanityList();
  }, []);

  const handleSave = async () => {
    const errors = {};
    if (!formData.nombre) errors.nombre = 'El nombre es requerido.';
    if (!formData.apellido) errors.apellido = 'El apellido es requerido.';
    if (!validarRut(formData.rut)) errors.rut = 'Ingresa un RUT válido.';
    if (!formData.cargo) errors.cargo = 'El cargo es requerido.';
    if (!validarEmail(formData.email)) errors.email = 'Ingresa un email válido.';

    const offensive = contienePalabrasOfensivas(`${formData.nombre} ${formData.apellido} ${formData.cargo}`, profanityList);
    if (offensive) {
      errors.nombre = `Referencia inapropiada detectada ("${offensive}").`;
    }

    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }
    setFormErrors({});
    const nombreCompleto = `${formData.nombre.trim()} ${formData.apellido.trim()}`;
    const url = isEditMode ? `${apiBase}/api/funcionarios/update/${editingId}` : `${apiBase}/api/funcionarios`;
    const method = 'POST';

    console.log(`[DEBUG] Intentando ${method} a: ${url}`);
    console.log('[DEBUG] Body:', { nombre: nombreCompleto, email: formData.email, rut: formData.rut, cargo: formData.cargo });

    try {
      const resp = await fetch(url, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nombre: nombreCompleto,
          email: formData.email,
          rut: formData.rut,
          cargo: formData.cargo
        })
      });
      console.log('[DEBUG] Respuesta recibida:', resp.status);

      if (resp.ok) {
        setIsModalOpen(false);
        setIsEditMode(false);
        setEditingId(null);
        setFormData({ nombre: '', apellido: '', email: '', rut: '', cargo: '' });
        fetchFuncionarios();
      } else {
        const contentType = resp.headers.get("content-type");
        if (contentType && contentType.indexOf("application/json") !== -1) {
          const errorData = await resp.json();
          alert(`Error del Servidor: ${errorData.error}`);
        } else {
          const textError = await resp.text();
          console.error('[DEBUG] Error No-JSON:', textError);
          alert(`Error Inesperado (HTML): ${resp.status}. Revisa la consola.`);
        }
      }
    } catch (err) {
      console.error('[DEBUG] Error crítico de red:', err);
      alert('Error de red inalcanzable. Revisa la consola o si el backend está activo.');
    }
  };

  const handleOpenHistory = async (funcionario) => {
    setSelectedFuncionario(funcionario);
    setHistoryModalOpen(true);
    setIsEquiposLoading(true);
    try {
      const resp = await fetch(`${apiBase}/api/funcionarios/${funcionario.id}/equipos`);
      if (resp.ok) {
        const data = await resp.json();
        setFuncionarioEquipos(data);
      }
    } catch (err) {
      console.error(err);
      alert('Error obteniendo historial de equipos');
    } finally {
      setIsEquiposLoading(false);
    }
  };

  const handleEdit = (f) => {
    const parts = f.nombre.split(' ');
    const firstName = parts[0] || '';
    const lastName = parts.slice(1).join(' ') || '';

    setFormData({
      nombre: firstName,
      apellido: lastName,
      email: f.email,
      rut: f.rut || '',
      cargo: f.cargo || ''
    });
    setEditingId(f.id);
    setIsEditMode(true);
    setIsModalOpen(true);
  };

  const handleDelete = async (id, nombre) => {
    if (!window.confirm(`¿Seguro que deseas eliminar definitivamente a: ${nombre}?`)) return;
    try {
      const resp = await fetch(`${apiBase}/api/funcionarios/${id}`, { method: 'DELETE' });
      if (resp.ok) {
        fetchFuncionarios();
      } else {
        alert('No se pudo borrar el registro.');
      }
    } catch (err) {
      console.error(err);
      alert('Error contactando al servidor.');
    }
  };

  const uniqueCargos = Array.from(new Set(funcionarios.map(f => f.cargo).filter(Boolean))).sort();

  const filteredFuncionarios = funcionarios.filter(f => {
    const term = searchTerm.toLowerCase();
    const matchSearch = f.nombre.toLowerCase().includes(term) ||
      (f.equipos_asignados || "").toLowerCase().includes(term);
    const matchCargo = !filtroCargo || f.cargo === filtroCargo;
    return matchSearch && matchCargo;
  });

  return (
    <div className="dashboard-layout">
      <aside className="sidebar">
        <div className="sidebar-header">
          <h2>Gestor de Equipos</h2>
        </div>
        <nav className="sidebar-nav">
          <Link to="/dashboard" className="nav-item">Equipos</Link>
          <Link to="/funcionarios" className="nav-item active">Funcionarios</Link>
          {(JSON.parse(localStorage.getItem('user') || '{}').rol === 'admin' ||
            JSON.parse(localStorage.getItem('user') || '{}').rol === 'superadmin') && (
              <>
                <Link to="/supervisor" className="nav-item">Panel de Análisis</Link>
                <Link to="/usuarios" className="nav-item">Usuarios (Admin)</Link>
              </>
            )}
        </nav>
      </aside>

      <main className="main-content">
        <header className="content-header">
          <h1>Registro de Empleados Locales</h1>
          <Link to="/perfil" className="user-profile" style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>{JSON.parse(localStorage.getItem('user') || '{}').nombre || 'Mi Perfil'}</span>
            <div className="avatar" style={{ background: '#0d6efd' }}>{JSON.parse(localStorage.getItem('user') || '{}').nombre?.charAt(0).toUpperCase() || 'P'}</div>
          </Link>
        </header>

        <section className="datatable-section" style={{ marginTop: '30px' }}>
          <div className="datatable-header">
            <h2>Asignaciones Activas por Custodio</h2>
            <div className="header-actions">
              <div className="search-bar">
                <input
                  type="text"
                  placeholder="Buscar por funcionario o equipo..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <select
                className="filter-select"
                value={filtroCargo}
                onChange={(e) => setFiltroCargo(e.target.value)}
                style={{ padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '14px' }}
              >
                <option value="">Todos los Cargos</option>
                {uniqueCargos.map(cargo => (
                  <option key={cargo} value={cargo}>{cargo}</option>
                ))}
              </select>
              <button className="btn-primary" onClick={() => { setIsEditMode(false); setEditingId(null); setFormData({ nombre: '', apellido: '', email: '', rut: '', cargo: '' }); setIsModalOpen(true); }}>+ Añadir Funcionario</button>
            </div>
          </div>

          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Nombre Completo</th>
                  <th>RUT</th>
                  <th>Cargo</th>
                  <th>Contacto Oficial</th>
                  <th style={{ textAlign: 'center' }}>Equipos</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>Analizando registros de inventario...</td></tr>
                ) : filteredFuncionarios.length > 0 ? (
                  filteredFuncionarios.map((f, i) => (
                    <tr key={f.id || i}>
                      <td><strong>{f.nombre}</strong></td>
                      <td style={{ fontSize: '13px', color: '#444' }}>{f.rut || 'N/A'}</td>
                      <td style={{ fontSize: '13px', fontWeight: '500' }}>{f.cargo || 'Funcionario'}</td>
                      <td style={{ color: '#6c757d' }}>{f.email}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span className="badge category-badge" style={{ background: f.cantidad_equipos > 0 ? '#0d6efd' : '#94a3b8', color: 'white' }}>
                          {f.cantidad_equipos}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button className="btn-secondary" onClick={() => handleEdit(f)}>
                            Editar
                          </button>
                          <button className="btn-secondary" onClick={() => handleOpenHistory(f)}>
                            Historial
                          </button>
                          <button className="btn-danger btn-primary" onClick={() => handleDelete(f.id, f.nombre)}>
                            Borrar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="no-results">No hay empleados habilitados para visualización.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{isEditMode ? 'Editar Funcionario' : 'Registrar Nuevo Funcionario'}</h2>
              <button className="close-btn" onClick={() => { setIsModalOpen(false); setIsEditMode(false); setEditingId(null); }}>×</button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="crud-form">
              <div className="form-row">
                <div className="input-group">
                  <label>Nombre(s) *</label>
                  <input type="text" value={formData.nombre} onChange={(e) => setFormData({ ...formData, nombre: e.target.value })} placeholder="Ej: Daniel" style={formErrors.nombre ? { borderColor: '#dc3545' } : {}} />
                  {formErrors.nombre && <small style={{ color: '#dc3545' }}>{formErrors.nombre}</small>}
                </div>
                <div className="input-group">
                  <label>Apellido(s) *</label>
                  <input type="text" value={formData.apellido} onChange={(e) => setFormData({ ...formData, apellido: e.target.value })} placeholder="Ej: Sánchez" style={formErrors.apellido ? { borderColor: '#dc3545' } : {}} />
                  {formErrors.apellido && <small style={{ color: '#dc3545' }}>{formErrors.apellido}</small>}
                </div>
              </div>
              <div className="form-row">
                <div className="input-group">
                  <label>RUT *</label>
                  <input
                    type="text"
                    value={formData.rut}
                    onChange={(e) => setFormData({ ...formData, rut: e.target.value })}
                    onBlur={(e) => setFormData({ ...formData, rut: formatearRut(e.target.value) })}
                    placeholder="12.345.678-9"
                    style={formErrors.rut ? { borderColor: '#dc3545' } : {}}
                  />
                  {formErrors.rut && <small style={{ color: '#dc3545' }}>{formErrors.rut}</small>}
                </div>
                <div className="input-group">
                  <label>Cargo / Departamento *</label>
                  <input
                    type="text"
                    value={formData.cargo}
                    onChange={(e) => setFormData({ ...formData, cargo: e.target.value })}
                    placeholder="Ej: Analista de Sistemas"
                    style={formErrors.cargo ? { borderColor: '#dc3545' } : {}}
                  />
                  {formErrors.cargo && <small style={{ color: '#dc3545' }}>{formErrors.cargo}</small>}
                </div>
              </div>
              <div className="form-row">
                <div className="input-group full-width">
                  <label>Email de Contacto *</label>
                  <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="funcionario@municipalidad.cl" style={formErrors.email ? { borderColor: '#dc3545' } : {}} />
                  {formErrors.email && <small style={{ color: '#dc3545' }}>{formErrors.email}</small>}
                </div>
              </div>
              <div className="form-footer">
                <button type="button" className="btn-secondary" onClick={() => { setIsModalOpen(false); setIsEditMode(false); setEditingId(null); setFormErrors({}); }}>Cancelar</button>
                <button type="submit" className="btn-primary">{isEditMode ? 'Guardar Cambios' : 'Guardar Funcionario'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {historyModalOpen && selectedFuncionario && (
        <div className="modal-overlay">
          <div className="modal-content detail-modal" style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <div className="avatar" style={{ background: '#0d6efd', width: '45px', height: '45px', fontSize: '18px' }}>{selectedFuncionario.nombre.charAt(0)}</div>
                <div>
                  <h2 style={{ margin: 0 }}>{selectedFuncionario.nombre}</h2>
                  <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>{selectedFuncionario.cargo || 'Funcionario Municipal'}</p>
                </div>
              </div>
              <button className="close-btn" onClick={() => setHistoryModalOpen(false)}>×</button>
            </div>

            <div className="detail-body" style={{ padding: '25px' }}>
              <div className="profile-summary" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px' }}>
                <div className="info-box-minimal" style={{ padding: '15px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                  <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 'bold', textTransform: 'uppercase' }}>Datos de Contacto</span>
                  <p style={{ margin: '5px 0 0 0', fontSize: '14px' }}><strong>RUT:</strong> {selectedFuncionario.rut || 'No registrado'}</p>
                  <p style={{ margin: '2px 0 0 0', fontSize: '14px' }}><strong>Email:</strong> {selectedFuncionario.email}</p>
                </div>
                <div className="info-box-minimal" style={{ padding: '15px', background: '#f0f9ff', borderRadius: '10px', border: '1px solid #bae6fd' }}>
                  <span style={{ fontSize: '11px', color: '#0369a1', fontWeight: 'bold', textTransform: 'uppercase' }}>Resumen de Cargos</span>
                  <p style={{ margin: '5px 0 0 0', fontSize: '14px' }}><strong>Total Equipos:</strong> {funcionarioEquipos.length}</p>
                  <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#0c4a6e' }}>
                    {Array.from(new Set(funcionarioEquipos.map(e => e.categoria))).slice(0, 3).join(', ') || 'Sin equipos'}
                  </p>
                </div>
              </div>

              <h3 style={{ fontSize: '16px', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ width: '8px', height: '8px', background: '#0d6efd', borderRadius: '50%' }}></span>
                Inventario Actual bajo su responsabilidad
              </h3>

              {isEquiposLoading ? (
                <div style={{ textAlign: 'center', padding: '30px' }}>
                  <div className="loader-mini" style={{ border: '3px solid #f3f3f3', borderTop: '3px solid #0d6efd', borderRadius: '50%', width: '24px', height: '24px', animation: 'spin 1s linear infinite', margin: '0 auto' }}></div>
                </div>
              ) : funcionarioEquipos.length > 0 ? (
                <div className="equipment-mini-list" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {funcionarioEquipos.map((eq, idx) => (
                    <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 15px', background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                      <div>
                        <div style={{ fontWeight: '700', fontSize: '14px' }}>{eq.codigo} - {eq.modelo}</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>{eq.categoria} • Ingreso: {eq.fecha_asignacion}</div>
                      </div>
                      <span className={`badge status-${(eq.estado || 'operativo').toLowerCase().replace(' ', '-')}`} style={{ fontSize: '10px' }}>{eq.estado}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', background: '#f8fafc', borderRadius: '15px', border: '1px dashed #cbd5e1' }}>
                  <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>Este funcionario no tiene equipos asignados actualmente.</p>
                </div>
              )}

              <div style={{ marginTop: '30px', padding: '15px', background: '#fffbeb', borderRadius: '10px', border: '1px solid #fef3c7', display: 'flex', gap: '12px', alignItems: 'center' }}>
                <p style={{ fontSize: '12px', color: '#92400e', margin: 0 }}>
                  Esta vista es de <strong>solo lectura</strong>. Para reasignar o dar de baja equipos, utiliza el panel principal de Equipos.
                </p>
              </div>
            </div>

            <div className="modal-footer" style={{ padding: '15px 25px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn-secondary" onClick={() => setHistoryModalOpen(false)}>Cerrar Ficha</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Funcionarios;
